// DOMPurify is only used in browser context for additional safety
import DOMPurify from 'dompurify';
import {logError, logWarn} from './logger';

/**
 * Sanitize CSS to prevent XSS attacks and data exfiltration
 *
 * Security concerns with custom CSS:
 * 1. JavaScript execution via url() with javascript: protocol
 * 2. Data exfiltration via background-image, @import with URLs to attacker servers
 * 3. UI redressing attacks with position, z-index, opacity
 * 4. Content injection via ::before/::after pseudo-elements
 *
 * This sanitizer:
 * - Removes dangerous CSS properties that can exfiltrate data
 * - Blocks external URLs (http/https) to prevent data exfiltration
 * - Allows only safe CSS properties and values
 * - Validates syntax to prevent injection attacks
 */

/**
 * Dangerous CSS properties that can be used for data exfiltration or XSS.
 * Matched as case-insensitive substrings of the CSS body, so the names
 * here must NOT appear as substrings of legitimate CSS — that's why
 * "import" is not in this list (it would match `!important`); the
 * @import at-rule is caught separately by containsDangerousPattern.
 */
const DANGEROUS_PROPERTIES = [
    'behavior', // IE-specific, can execute scripts
    'binding', // XBL binding, can execute scripts
    '-moz-binding', // Mozilla XBL binding
];

/**
 * Properties that can load external resources (potential data exfiltration)
 */
const EXTERNAL_RESOURCE_PROPERTIES = [
    'background',
    'background-image',
    'border-image',
    'cursor',
    'list-style-image',
    '@import',
];

/**
 * Check if a CSS value contains dangerous patterns
 */
function containsDangerousPattern(value: string): boolean {
    const dangerous = [
        /javascript:/i, // javascript: protocol
        /data:/i, // data: protocol (can contain scripts)
        /vbscript:/i, // vbscript: protocol
        /@import\s+url/i, // @import with URL
        /expression\s*\(/i, // CSS expressions (IE)
        /behavior\s*:/i, // IE behavior
        /binding\s*:/i, // XBL binding
        /-moz-binding/i, // Mozilla binding
        /url\s*\(\s*["']?\s*http/i, // External HTTP/HTTPS URLs
        /url\s*\(\s*["']?\s*\/\//i, // Protocol-relative URLs
    ];

    return dangerous.some((pattern) => pattern.test(value));
}

/**
 * Sanitize a single CSS rule (property: value pair)
 */
function sanitizeCSSRule(property: string, value: string): string | null {
    // Remove dangerous properties entirely
    if (DANGEROUS_PROPERTIES.some((prop) => property.toLowerCase().includes(prop))) {
        logWarn('CSS sanitizer blocked dangerous property', {property});
        return null;
    }

    // Check for dangerous patterns in value
    if (containsDangerousPattern(value)) {
        logWarn('CSS sanitizer blocked dangerous value', {property});
        return null;
    }

    // For properties that can load external resources, only allow relative URLs or none
    if (EXTERNAL_RESOURCE_PROPERTIES.some((prop) => property.toLowerCase().includes(prop))) {
        // Allow "none" and relative URLs only (starting with / or ./)
        const urlMatch = value.match(/url\s*\(\s*["']?([^"')]+)["']?\s*\)/i);
        if (urlMatch) {
            const url = urlMatch[1].trim();
            // Only allow relative URLs (starting with / or ./)
            if (!url.startsWith('/') && !url.startsWith('./') && !url.startsWith('../')) {
                logWarn('CSS sanitizer blocked external URL', {property});
                return null;
            }
        }
    }

    return `${property}: ${value}`;
}

/**
 * Sanitize custom CSS by parsing and filtering dangerous patterns
 *
 * @param css - The CSS string to sanitize
 * @returns Sanitized CSS string safe for injection
 */
export function sanitizeCSS(css: string): string {
    if (!css || typeof css !== 'string') {
        return '';
    }

    try {
        // First, use DOMPurify to remove any potential script-like content (browser only)
        // Configure DOMPurify for CSS mode
        let purified = css;
        if (typeof window !== 'undefined' && DOMPurify?.sanitize) {
            purified = DOMPurify.sanitize(css, {
                ALLOWED_TAGS: [], // No HTML tags allowed in CSS
                ALLOWED_ATTR: [],
                KEEP_CONTENT: true,
            });
        }

        // Remove @import rules entirely (prevent external stylesheet loading)
        const cleaned = purified.replace(/@import[^;]+;?/gi, '');

        // Process CSS while maintaining structure
        const result: string[] = [];
        let currentBlock: string[] = [];
        let insideBlock = false;
        let braceDepth = 0;

        // Split into tokens while preserving structure
        const tokens = cleaned.split(/([{};])/);

        for (let i = 0; i < tokens.length; i++) {
            const token = tokens[i].trim();
            if (!token) continue;

            if (token === '{') {
                // Opening brace - start a new rule block
                braceDepth++;
                insideBlock = true;
                result.push(token);
            } else if (token === '}') {
                // Closing brace - end the rule block
                braceDepth--;
                if (braceDepth === 0) {
                    insideBlock = false;
                }
                // Add any accumulated declarations
                if (currentBlock.length > 0) {
                    result.push(currentBlock.join('; '));
                    currentBlock = [];
                }
                result.push(token);
            } else if (token === ';') {
                // Semicolon inside block - just a separator, skip it
                // (we'll add semicolons when joining declarations)
                continue;
            } else if (insideBlock && token.includes(':')) {
                // This is a property declaration
                const colonIndex = token.indexOf(':');
                const property = token.substring(0, colonIndex).trim();
                const value = token.substring(colonIndex + 1).trim();

                const sanitizedRule = sanitizeCSSRule(property, value);
                if (sanitizedRule) {
                    currentBlock.push(sanitizedRule);
                }
            } else if (!insideBlock) {
                // This is a selector or at-rule
                if (token.toLowerCase().startsWith('@import')) {
                    logWarn('CSS sanitizer blocked @import rule');
                    continue;
                }
                result.push(token);
            } else {
                // Inside block but not a valid property - might be a value continuation
                // Add to previous declaration if exists
                if (currentBlock.length > 0) {
                    currentBlock[currentBlock.length - 1] += ' ' + token;
                }
            }
        }

        // Join with proper formatting.  We track block depth here too: a
        // token that contains `:` is a *property declaration* only when
        // we're already inside a block.  Outside a block, `:root`,
        // `:hover`, `::before` etc. are pseudo-class/element selectors
        // and must NOT get a trailing `;`.  Without this depth check,
        // `:root` was being emitted as `:root;` which is invalid CSS,
        // silently breaking every tenant override of --mld-* variables.
        let formatted = '';
        let depth = 0;
        for (let i = 0; i < result.length; i++) {
            const token = result[i];

            if (token === '{') {
                depth++;
                formatted += ' {\n  ';
            } else if (token === '}') {
                depth--;
                formatted = formatted.trimEnd();
                if (!formatted.endsWith(';')) {
                    formatted += ';';
                }
                formatted += '\n}\n';
            } else if (depth > 0 && token.includes(':')) {
                // Property declaration (inside a block).
                formatted += token + ';';
                if (i + 1 < result.length && result[i + 1].includes(':')) {
                    formatted += '\n  ';
                }
            } else {
                // Selector, at-rule, or other top-level token.
                formatted += token;
            }
        }

        // Clean up extra whitespace
        formatted = formatted
            .replace(/\n\s*\n/g, '\n') // Remove multiple blank lines
            .replace(/;\s*;/g, ';') // Remove duplicate semicolons
            .replace(/{\s*;/g, '{') // Remove semicolon after opening brace
            .trim();

        // Final validation: ensure no dangerous patterns slipped through
        if (containsDangerousPattern(formatted)) {
            logError('CSS sanitizer rejected output for dangerous pattern');
            return '';
        }

        return formatted;
    } catch (error) {
        logError('CSS sanitizer error', error);
        // On error, return empty string to be safe
        return '';
    }
}

/**
 * Validate that CSS is safe before allowing it to be saved
 * Returns true if CSS is safe, false otherwise
 */
export function validateCSS(css: string): {valid: boolean; error?: string} {
    if (!css || typeof css !== 'string') {
        return {valid: true}; // Empty CSS is valid
    }

    // Check length limit (prevent DoS)
    if (css.length > 50000) {
        return {valid: false, error: 'CSS exceeds maximum length of 50KB'};
    }

    // Check for dangerous patterns
    if (containsDangerousPattern(css)) {
        return {valid: false, error: 'CSS contains dangerous patterns (external URLs, scripts, etc.)'};
    }

    // Check for dangerous properties
    const lowerCSS = css.toLowerCase();
    for (const prop of DANGEROUS_PROPERTIES) {
        if (lowerCSS.includes(prop)) {
            return {valid: false, error: `CSS contains dangerous property: ${prop}`};
        }
    }

    // Try to sanitize - if result is empty, the CSS was all dangerous
    const sanitized = sanitizeCSS(css);
    if (css.trim() && !sanitized.trim()) {
        return {valid: false, error: 'CSS was entirely rejected during sanitization'};
    }

    return {valid: true};
}
